import { useState, useEffect, useMemo } from 'react';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';
import { Loader } from '../../components/ui/loader';

// icons
import { 
  SearchNormal1,
  FilterSearch,
  Refresh2,
  Calendar,
  DocumentText
} from 'iconsax-react';

// project imports
import { getAuditLogs, getOrganizations } from '../../services/subscriptionService';

const AuditTrail = () => {
  const { toast } = useToast();

  // State
  const [logs, setLogs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [selectedChangeType, setSelectedChangeType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);

  // Debounced fetch for search
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(handler);
  }, [selectedOrgId, selectedChangeType, page, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (selectedOrgId !== 'all') params.orgId = selectedOrgId;
      if (selectedChangeType !== 'all') params.changeType = selectedChangeType;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const [logsRes, orgsRes] = await Promise.all([
        getAuditLogs(params),
        getOrganizations()
      ]);

      setLogs(logsRes.data?.logs || logsRes.data || []);
      setTotalPages(logsRes.data?.totalPages || Math.ceil((logsRes.data?.logs?.length || logsRes.data?.length || 0) / rowsPerPage));
      setOrgs(orgsRes.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load plan change compliance logs."
      });
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeBadge = (type) => {
    switch (type) {
      case 'plan_upgrade':
        return (
          <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200">
            Plan Upgrade
          </Badge>
        );
      case 'plan_downgrade':
        return (
          <Badge className="bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border border-rose-200">
            Plan Downgrade
          </Badge>
        );
      case 'limit_override':
        return (
          <Badge className="bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400 border border-blue-200">
            Limit Override
          </Badge>
        );
      case 'feature_toggle':
        return (
          <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border border-amber-200">
            Feature Toggle
          </Badge>
        );
      case 'suspension':
        return (
          <Badge className="bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400 border border-red-200">
            Suspension
          </Badge>
        );
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  // Render previousState vs newState diff comparison
  const renderDiff = (log) => {
    const prev = log.previousState || {};
    const next = log.newState || {};
    
    // Union of all keys, with defensive check for objects
    if (typeof prev !== 'object' || typeof next !== 'object') {
      return <span className="text-xs text-muted-foreground">Complex state changed</span>;
    }

    const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));
    const changedKeys = keys.filter(k => {
      // ignore helper database fields in diff
      if (k === '_id' || k === 'updatedAt' || k === 'createdAt') return false;
      return JSON.stringify(prev[k]) !== JSON.stringify(next[k]);
    });

    if (changedKeys.length === 0) {
      return <span className="text-xs text-muted-foreground">No properties changed</span>;
    }

    return (
      <div className="space-y-1.5 font-sans max-w-md">
        {changedKeys.map(k => {
          let prevVal = prev[k];
          let nextVal = next[k];

          // Format sub-objects (like overrides, features)
          if (prevVal instanceof Object && prevVal !== null) {
            prevVal = JSON.stringify(prevVal);
          }
          if (nextVal instanceof Object && nextVal !== null) {
            nextVal = JSON.stringify(nextVal);
          }

          // Format null/undefined
          const formatValue = (v) => {
            if (v === null || v === undefined) return 'None';
            if (v === true) return 'Enabled';
            if (v === false) return 'Disabled';
            return String(v);
          };

          return (
            <div key={k} className="flex flex-wrap items-center gap-1.5 text-xs border-b pb-0.5 last:border-0 border-slate-100 dark:border-slate-800">
              <span className="font-semibold text-slate-700 dark:text-slate-300">{k}:</span>
              <span className="text-rose-600 dark:text-rose-400 line-through bg-rose-50 dark:bg-rose-950/10 px-1 rounded">
                {formatValue(prevVal)}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/10 px-1 rounded">
                {formatValue(nextVal)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Server-side filtering applied, use logs directly
  const filteredLogs = logs;
  const paginatedLogs = logs;

  const handleClearFilters = () => {
    setSelectedOrgId('all');
    setSelectedChangeType('all');
    setSearchQuery('');
    setPage(0);
  };

  const isFiltered = selectedOrgId !== 'all' || selectedChangeType !== 'all' || searchQuery !== '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Subscription Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground">
            Immutable log of subscription tier changes, quotas overrides, and contract updates for compliance
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="flex items-center gap-1.5"
        >
          <Refresh2 size={16} />
          Refresh Log
        </Button>
      </div>

      {/* Filter Section */}
      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-col md:flex-row items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground min-w-[80px]">
            <FilterSearch size={18} />
            <span>Filters</span>
          </div>

          {/* Org Filter */}
          <div className="w-full md:w-[220px]">
            <Select value={selectedOrgId} onValueChange={(val) => { setSelectedOrgId(val); setPage(0); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Agencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agencies</SelectItem>
                {orgs.map(org => (
                  <SelectItem key={org._id} value={org._id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Change Type Filter */}
          <div className="w-full md:w-[200px]">
            <Select value={selectedChangeType} onValueChange={(val) => { setSelectedChangeType(val); setPage(0); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Operations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Operations</SelectItem>
                <SelectItem value="plan_upgrade">Plan Upgrade</SelectItem>
                <SelectItem value="plan_downgrade">Plan Downgrade</SelectItem>
                <SelectItem value="limit_override">Limit Override</SelectItem>
                <SelectItem value="feature_toggle">Feature Toggle</SelectItem>
                <SelectItem value="suspension">Suspension</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search Input */}
          <div className="relative w-full md:flex-grow">
            <SearchNormal1 size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input 
              placeholder="Search by agency, admin name, or change reason..." 
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              className="pl-9 h-10"
            />
          </div>

          {isFiltered && (
            <Button variant="ghost" onClick={handleClearFilters} className="text-muted-foreground h-10 px-3">
              Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <Loader message="Loading plan change compliance logs..." className="min-h-[300px]" />
          ) : filteredLogs.length === 0 ? (
            <div className="min-h-[300px] flex flex-col items-center justify-center gap-4 text-center p-8">
              <DocumentText size={48} className="text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg">No Audit Logs Found</h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-1">
                No matching subscription history logs were found for the selected filters.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead className="w-[200px]">Agency</TableHead>
                    <TableHead className="w-[150px]">Change Type</TableHead>
                    <TableHead>Quotas & Property Diffs</TableHead>
                    <TableHead className="w-[220px]">Justification Reason</TableHead>
                    <TableHead className="w-[180px]">Performed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(log.changedAt).toLocaleString()}
                        </div>
                        <span className="block mt-0.5 opacity-60">IP: {log.ipAddress}</span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{log.orgId?.name || 'Deleted Org'}</div>
                        <code className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1 py-0.5 rounded font-mono">
                          {log.orgId?.code || log.orgId}
                        </code>
                      </TableCell>
                      <TableCell>{getChangeTypeBadge(log.changeType)}</TableCell>
                      <TableCell className="py-3">{renderDiff(log)}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate italic" title={log.reason}>
                        "{log.reason}"
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-semibold text-foreground">
                          {log.changedBy ? log.changedBy.name : 'System'}
                        </div>
                        <div className="text-muted-foreground text-[10px]">{log.changedBy?.email || ''}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Showing logs for current page
                  </span>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-medium">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page === totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditTrail;
